// PRD §7.0.4 — Public mentor application form. Anonymous, no auth.
// Submits to POST /api/mentors/apply. On success, lands status='application'
// in the admin vetting queue. Tone: not "fill in 23 fields" bureaucracy —
// minimum viable so a senior operator finishes it on their phone in 90s.
import { useState } from "react";
import { Loader2, Sparkles, ChevronRight, Check as CheckIcon } from "lucide-react";

const BASE = import.meta.env.BASE_URL;

export default function MentorsApply() {
  const [form, setForm] = useState({
    fullName: "", email: "", headline: "", linkedinUrl: "",
    location: "", expertiseTags: "", whyHere: "", referredBy: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<{ alreadySubmitted: boolean } | null>(null);

  function update<K extends keyof typeof form>(k: K, v: string) { setForm(s => ({ ...s, [k]: v })); }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setErr(null);
    try {
      const r = await fetch(`${BASE}api/mentors/apply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          expertiseTags: form.expertiseTags.split(",").map(t => t.trim()).filter(Boolean),
        }),
      });
      const d = await r.json();
      if (!r.ok) { setErr(d.error || "Submission failed"); return; }
      setDone({ alreadySubmitted: !!d.alreadySubmitted });
    } catch {
      setErr("Network error — please try again.");
    } finally { setBusy(false); }
  }

  if (done) {
    return (
      <div data-mentor-theme="dark" className="min-h-screen bg-[var(--mentor-bg)] text-[var(--mentor-fg)] flex items-center justify-center px-6">
        <div className="max-w-lg text-center">
          <div className="w-14 h-14 rounded-full bg-[#FF5C00]/15 border border-[#FF5C00]/40 flex items-center justify-center mx-auto mb-6">
            <CheckIcon className="w-6 h-6 text-[#FF5C00]" />
          </div>
          <h1 className="text-2xl font-serif font-bold mb-3" data-testid="text-apply-success-title">
            {done.alreadySubmitted ? "We already have your application" : "Thank you — application received"}
          </h1>
          <p className="text-sm text-white/70 leading-relaxed">
            {done.alreadySubmitted
              ? "Looks like you've applied before. The PC team is on it — no need to resubmit."
              : "A member of our PC team will review your profile and get back to you within 5–7 days. We don't take everyone — but we read everyone."}
          </p>
          <p className="text-xs text-white/45 mt-6">Favcy Select · By invitation only</p>
        </div>
      </div>
    );
  }

  return (
    <div data-mentor-theme="dark" className="min-h-screen bg-[var(--mentor-bg)] text-[var(--mentor-fg)] relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-48 left-1/2 -translate-x-1/2 w-[820px] h-[820px] rounded-full bg-[#FF5C00]/[0.07] blur-3xl" />
        <div className="absolute bottom-0 -right-40 w-[520px] h-[520px] rounded-full bg-[#FF5C00]/[0.04] blur-3xl" />
      </div>

      <div className="relative z-10 max-w-2xl mx-auto px-6 lg:px-10 py-10 lg:py-16">
        <div className="flex items-center gap-2 mb-10">
          <div className="w-7 h-7 rounded bg-[#FF5C00] flex items-center justify-center text-white text-xs font-bold">F</div>
          <div>
            <div className="text-sm font-bold tracking-tight">Favcy Select</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-white/60">Mentor application</div>
          </div>
        </div>

        <div className="mb-8">
          <div className="inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.18em] text-[#FF5C00] border border-[#FF5C00]/30 rounded-full px-2.5 py-1 mb-4">
            <Sparkles className="w-3 h-3" /> Apply to mentor
          </div>
          <h1 className="text-3xl lg:text-4xl font-serif font-bold tracking-tight leading-[1.1] mb-3">
            Senior operators only.
          </h1>
          <p className="text-base text-white/75 leading-relaxed">
            We pair founders with people who've done the thing — not "advisors" who haven't. If that's you, this takes ninety seconds.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-4 bg-[var(--mentor-card)] border border-[var(--mentor-border)] rounded-xl p-5 lg:p-6">
          {err && (
            <div className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-md px-2.5 py-2" data-testid="error-mentor-apply">{err}</div>
          )}

          <Field label="Full name" required v={form.fullName} onC={v => update("fullName", v)} testId="input-apply-fullname" />
          <Field label="Email" required type="email" v={form.email} onC={v => update("email", v)} testId="input-apply-email" />
          <Field label="One-line headline" required v={form.headline} onC={v => update("headline", v)} testId="input-apply-headline"
            hint="e.g. CFO at Acme · ex-Stripe · 12 yrs in fintech" />
          <Field label="LinkedIn URL" v={form.linkedinUrl} onC={v => update("linkedinUrl", v)} testId="input-apply-linkedin" />
          <Field label="Location" v={form.location} onC={v => update("location", v)} testId="input-apply-location"
            hint="City, country" />
          <Field label="Expertise tags" v={form.expertiseTags} onC={v => update("expertiseTags", v)} testId="input-apply-tags"
            hint="Comma-separated: e.g. fintech, fundraising, growth, ops" />
          <Field label="Why you want to mentor" required multi v={form.whyHere} onC={v => update("whyHere", v)} testId="input-apply-why"
            hint="What do you bring? What kind of founder do you want to help?" />
          <Field label="Referred by" v={form.referredBy} onC={v => update("referredBy", v)} testId="input-apply-referredby"
            hint="Optional — name of the mentor or PC who pointed you here" />

          <button type="submit" disabled={busy}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#FF5C00] hover:bg-[#FF5C00]/90 disabled:opacity-30 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-md px-5 py-2.5 transition mt-2"
            data-testid="button-apply-submit">
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {busy ? "Submitting" : "Submit application"}
            {!busy && <ChevronRight className="w-4 h-4" />}
          </button>
          <p className="text-[11px] text-white/45 text-center leading-relaxed">
            We review every application personally. You'll hear back in 5–7 days.
          </p>
        </form>

        <div className="mt-8 text-center text-[11px] text-white/40">
          Favcy Select · A media-led venture builder
        </div>
      </div>
    </div>
  );
}

function Field({
  label, required, type, multi, v, onC, hint, testId,
}: {
  label: string;
  required?: boolean;
  type?: string;
  multi?: boolean;
  v: string;
  onC: (v: string) => void;
  hint?: string;
  testId: string;
}) {
  return (
    <label className="block">
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="text-[11px] font-mono uppercase tracking-[0.18em] text-white/65">
          {label}{required && <span className="text-[#FF5C00] ml-1">*</span>}
        </span>
        {hint && <span className="text-[10px] text-white/40 italic">{hint}</span>}
      </div>
      {multi ? (
        <textarea value={v} onChange={e => onC(e.target.value)} rows={4} required={required}
          className="w-full bg-black/30 border border-white/[0.12] rounded-md px-3 py-2 text-sm outline-none focus:border-[#FF5C00]/50 resize-none"
          data-testid={testId} />
      ) : (
        <input value={v} onChange={e => onC(e.target.value)} type={type || "text"} required={required}
          className="w-full bg-black/30 border border-white/[0.12] rounded-md px-3 py-2 text-sm outline-none focus:border-[#FF5C00]/50"
          data-testid={testId} />
      )}
    </label>
  );
}
